import {DIV, type HtmlTagName} from "./tags.js";
import type {DomEventName} from "./domEvents.js";

/**
 * Thin, testable wrapper around a DOM element. Components never touch `Element` directly,
 * which keeps their behaviour assertable without a browser and leaves room for other targets.
 */
export class DomNode {
    readonly element: HTMLElement;
    readonly #disposers: (() => void)[] = [];

    constructor(tag: HtmlTagName = DIV, cssClasses: readonly string[] = []) {
        this.element = document.createElement(tag);
        this.element.classList.add("juis", ...cssClasses.map(toDashCase));
    }

    setText(text: string): void {
        this.element.textContent = text;
    }

    getText(): string {
        return this.element.textContent ?? "";
    }

    addCssClass(cssClass: string): void {
        this.element.classList.add(toDashCase(cssClass));
    }

    removeCssClass(cssClass: string): void {
        this.element.classList.remove(toDashCase(cssClass));
    }

    toggleCssClass(cssClass: string, active: boolean): void {
        this.element.classList.toggle(toDashCase(cssClass), active);
    }

    hasCssClass(cssClass: string): boolean {
        return this.element.classList.contains(toDashCase(cssClass));
    }

    setAttribute(name: string, value: string | null): void {
        if (value === null) {
            this.element.removeAttribute(name);
        } else {
            this.element.setAttribute(name, value);
        }
    }

    setStyle(property: string, value: string): void {
        this.element.style.setProperty(property, value);
    }

    appendChild(child: DomNode): void {
        this.element.appendChild(child.element);
    }

    insertBefore(child: DomNode, reference: DomNode): void {
        this.element.insertBefore(child.element, reference.element);
    }

    replaceChild(newChild: DomNode, oldChild: DomNode): void {
        this.element.replaceChild(newChild.element, oldChild.element);
    }

    removeChild(child: DomNode): void {
        if (child.element.parentNode === this.element) {
            this.element.removeChild(child.element);
        }
    }

    /** Registers a DOM listener that is removed when the node is destroyed. */
    on<K extends DomEventName>(
        type: K,
        handler: (event: HTMLElementEventMap[K]) => void
    ): () => void {
        this.element.addEventListener(type, handler as EventListener);
        const dispose = () => this.element.removeEventListener(type, handler as EventListener);
        this.#disposers.push(dispose);
        return dispose;
    }

    destroy(): void {
        for (const dispose of this.#disposers) {
            dispose();
        }
        this.#disposers.length = 0;
        this.element.remove();
    }
}

const toDashCase = (value: string): string =>
    value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
